<template>
  <div :class="classes">
    <div v-if="message.content.isRoutine" ref="productItemRoutineHeader" class="products-item-routine-header">
      <div v-if="item.routineTitle" class="products-item-routine-title">{{ item.routineTitle }}</div>
      <!-- eslint-disable vue/no-v-html -->
      <div v-if="item.routineDescription" class="products-item-routine-description"
           v-html="sanitizedRoutineDescriptionMarkup" />
      <!-- eslint-enable -->
    </div>
    <div v-if="item.isEmpty" class="products-item-routine-empty-message">
      <div class="products-item-routine-empty-message-icon">⚠</div>
      <div class="products-item-routine-empty-message-text">{{ $t('product.emptyRoutineItem') }}</div>
    </div>
    <div
      v-else
      ref="productItemContainer"
      class="products-item-container"
      :style="containerHeight">
      <div class="products-item-header">
        <div class="products-item-badges" @click="clickOnItem">
          <badge
            v-for="productBadge in productBadges"
            :key="productBadge.featureFlagName"
            :badge-type="productBadge.badge.type"
            :text="productBadge.badge.text"
            :color="productBadge.badge.color"
          />
        </div>
        <explanation-button
          v-if="!!item.explanation"
          role="tooltip"
          :aria-labelledby="explanationId"
          :open="explanationVisible"
          :on-click="explanationButtonClick"
          @esc-pressed="closeExplanation"
        />
        <explanation
          v-if="!!item.explanation"
          :id="explanationId"
          :item="item"
          :message="message"
          :is-visible="explanationVisible"
        />
        <a
          :href="productUrl"
          :target="productTarget"
          :title="item.title"
          class="products-item-image-link"
          @click="clickOnItem"
          @keydown.space.prevent="clickOnItem"
        >
          <div
            class="products-item-image"
            :style="{ 'background-image': 'url(\'' + item.image_url + '\')' }"
          />
        </a>
      </div>
      <div class="products-item-body">
        <div class="products-item-body-top-container">
          <div class="products-item-body-top-container-header">
            <div class="products-item-title-and-subtitle">
              <div class="products-item-title" @click="clickOnItem">{{ item.title }}</div>
              <div v-if="!!item.subtitle" class="products-item-subtitle" @click="clickOnItem">{{ item.subtitle }}</div>
            </div>
          </div>
          <div v-if="showCustomerRatings" class="products-item-star-rating">
            <star-rating
              :inline="true"
              :star-size="10"
              :rating="Number(item.customerRating)"
              :read-only="true"
              :increment="0.01"
              :fixed-points="1"
              text-class="products-item-rating"
              :rounded-corners="true"
              :border-width="2"
              active-color="#505052"
              border-color="#505052"
              inactive-color="#EFF3F6"
            />
            <span v-if="showCustomerRatingCount" class="products-item-rating-count">({{
              item.customerRatingCount
            }})</span>
          </div>
          <div v-if="showPrice" class="products-item-price">
            <price :item="item" />
          </div>
        </div>
        <div v-if="showAttributeMatching" class="products-item-body-middle-container">
          <attribute-matching
            v-if="showAttributeMatching"
            role="button"
            tabindex="0"
            :item="item"
            :message="message"
            :is-list-expanded="isAttributeMatchingListExpanded"
            :keep-expanded="keepAttributeMatchingExpanded"
            :aria-expanded="matchingListAriaExpanded"
            :aria-controls="matchingListId"
            :matching-list-title-id="matchingListTitleId"
            @click.native="attributeMatchingClick"
            @keydown.native.enter.prevent="attributeMatchingClick"
            @keydown.native.space.prevent="attributeMatchingClick"
          />
        </div>
        <attribute-matching-list
          v-if="showAttributeMatching"
          :id="matchingListId"
          :aria-labelledby="matchingListTitleId"
          :is-list-expanded="isAttributeMatchingListExpanded"
          :attribute-matches-success="attributeMatchesSuccess"
          :attribute-matches-fail="attributeMatchesFail" />
        <div class="products-item-body-bottom-container">
          <div class="products-item-body-bottom-container-items">
            <div class="products-item-body-bottom-container-item-button">
              <button
                type="button"
                class="products-item-button"
                :data-product-identifier="item.productIdentifier"
                :data-product-identifier-2="item.productIdentifier2"
                @click="clickOnItem"
              >
                <span class="products-item-button-text">{{ productDetailPageButtonTitle }}</span>
              </button>
            </div>
            <div v-if="showAddToCartButton" class="products-item-body-bottom-container-add-to-cart">
              <add-to-cart-button
                :item="item"
                :message="message"
                @product-added-to-cart="$emit('product-added-to-cart', $event)"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
// @flow
import { mapGetters, mapState } from 'vuex'
import StarRating from 'vue-star-rating'

import { createClickout } from '../../../../../common/api/chatchampApi'
import { sanitizedMarkup } from '../../../../../common/utils'
import ExplanationButton from './ExplanationButton/ExplanationButton.vue'
import Explanation from './Explanation/Explanation.vue'
import AttributeMatching from './AttributeMatching/AttributeMatching.vue'
import AttributeMatchingList from './AttributeMatchingList/AttributeMatchingList.vue'
import AddToCartButton from './AddToCartButton/AddToCartButton.vue'
import Price from './Price/Price.vue'
import Badge from './Badge/Badge.vue'

export default {
  components: {
    AddToCartButton,
    AttributeMatching,
    AttributeMatchingList,
    Explanation,
    ExplanationButton,
    StarRating,
    Price,
    Badge
  },
  props: {
    item: {
      type: Object, // ProductItem
      default: () => {}
    },
    message: {
      type: Object,
      default: () => {}
    },
    keepAttributeMatchingExpanded: {
      type: Boolean,
      default: () => false
    },
    isAddToCartDisabled: {
      type: Boolean,
      default: () => false
    }
  },
  data: () => {
    return {
      explanationVisible: false,
      isAttributeMatchingListExpanded: false,
      containerHeight: { '--container-height': 'auto' }
    }
  },
  computed: {
    ...mapState(['customerId', 'subscriberId']),
    ...mapGetters(['productClickAction', 'isEnabledAddToCart']),
    showCustomerRatings (): boolean {
      return !!this.message.content.showCustomerRatings && Number(this.item.customerRating) > 0
    },
    showCustomerRatingCount (): boolean {
      return Number(this.item.customerRatingCount) > 0
    },
    attributeMatchesSuccess (): Array {
      return this.item.attributeMatches.filter(attrMatch => attrMatch.utility > 0 && attrMatch.translation)
    },
    attributeMatchesFail (): Array {
      return this.item.attributeMatches.filter(attrMatch => attrMatch.utility <= 0 && attrMatch.translation)
    },
    showAttributeMatching (): boolean {
      return process.env.FEATURE_FLAG_ENABLE_ATTRIBUTE_MATCHING && this.item.attributeMatches && (this.attributeMatchesSuccess.length > 0 || this.attributeMatchesFail.length > 0)
    },
    sanitizedRoutineDescriptionMarkup (): string {
      return sanitizedMarkup(this.item.routineDescription, this.message.content.messageId)
    },
    showAddToCartButton (): boolean {
      return this.isEnabledAddToCart && !this.isAddToCartDisabled
    },
    showPrice (): boolean {
      return !!this.item.price
    },
    productDetailPageButtonTitle (): string {
      return this.message.content.productDetailPageButtonTitle || this.$t('product.ctaButtonTitle')
    },
    productBadges (): Array<Object> {
      return [
        {
          featureFlagName: 'best-fit-badge',
          badge: this.item.badges?.find(badge => badge.type === 'best_fit')
        },
        {
          featureFlagName: 'best-price-badge',
          badge: this.item.badges?.find(badge => badge.type === 'best_price')
        },
        {
          featureFlagName: 'best-fit-for-price-badge',
          badge: this.item.badges?.find(badge => badge.type === 'best_fit_for_price')
        }
      ].filter(productBadge => !!productBadge.badge)
    },
    classes (): Object {
      return {
        'products-item': true,
        'products-item-routine': this.message.content.isRoutine
      }
    },
    explanationId () {
      return `explanation-${this.message.content.mid}-${this.item.productId}`
    },
    clickOpenExplanation (): boolean {
      return this.productClickAction === 'openExplanation'
    },
    productUrl (): string {
      if (this.clickOpenExplanation) return '#'
      const { productUrl } = this.item

      return productUrl
    },
    productTarget (): string {
      if (this.clickOpenExplanation) return '_self'

      return '_blank'
    },
    matchingListAriaExpanded () {
      return this.isAttributeMatchingListExpanded ? 'true' : 'false'
    },
    matchingListId () {
      return `matching-list-${this.message.content.mid}-${this.item.productId}`
    },
    matchingListTitleId () {
      return `matching-list-title-${this.message.content.mid}-${this.item.productId}`
    }
  },
  mounted: function () {
    if (!this.keepAttributeMatchingExpanded && this.$refs.productItemContainer) {
      this.containerHeight = { '--container-height': this.$refs.productItemContainer.clientHeight + 'px' }
    }
    if (this.keepAttributeMatchingExpanded) this.isAttributeMatchingListExpanded = true
  },
  methods: {
    clickOnItem (event): void {
      event.preventDefault()
      event.stopPropagation()
      if (this.clickOpenExplanation) {
        this.explanationButtonClick()
      } else {
        this.productClick()
      }
    },
    explanationButtonClick (): void {
      const { productId, productIdentifier } = this.item

      this.explanationVisible = !this.explanationVisible
      if (this.explanationVisible) {
        if (this.clickOpenExplanation) {
          createClickout(this.customerId, this.subscriberId, this.message.content.messageId, {
            productId,
            buttonType: 'explanation'
          })
          this.$emit('product-clicked', { productId, productIdentifier, ...(this.message && { chatbotId: this.message.content.chatbotId }) })
        } else {
          this.$emit('product-info-clicked', { productId, ...(this.message && { chatbotId: this.message.content.chatbotId }) })
        }
      }
    },
    attributeMatchingClick (): void {
      this.isAttributeMatchingListExpanded = !this.isAttributeMatchingListExpanded
      if (this.isAttributeMatchingListExpanded) {
        this.$emit('attribute-matching-list-expanded', { productId: this.item.productId, ...(this.message && { chatbotId: this.message.content.chatbotId }) })
      }
    },
    productClick (): void {
      const { productId, productIdentifier, productUrl } = this.item
      createClickout(this.customerId, this.subscriberId, this.message.content.messageId, {
        productId,
        buttonType: 'url',
        url: productUrl
      })
      this.$emit('product-clicked', { productId, productIdentifier, ...(this.message && { chatbotId: this.message.content.chatbotId }) })
      window.open(productUrl)
    },
    closeExplanation () {
      this.explanationVisible = false
    }
  }
}
</script>

<style src="./products-item.scss" />
